import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
    MAT_DIALOG_DATA,
    MatDialogActions,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle,
} from '@angular/material/dialog';
import { Subject, takeUntil, tap } from 'rxjs';
import {
    CardTypeEnum,
    CardTypeLabelMapping,
} from '../../../../../../core/enums/commerce/cards-on-file/card-types';
import { formatNumericalMonthToMM } from '../../../../../../core/functions/date-helpers';
import { AuthenticatedUser } from '../../../../../../core/models/authenticated-user';
import { CardOnFileResponse } from '../../../../../../core/models/commerce/cards-on-file/card-on-file-response';
import { AlertService } from '../../../../../../core/services/alert/alert.service';
import { CreditCardService } from '../../../../../../core/services/commerce/credit-card/credit-card.service';
import { CardTypeIconComponent } from '../../../../../../shared/components/card-type-icon/card-type-icon.component';
import { DraggableModalWrapperComponent } from '../../../../../../shared/components/modal/draggable-modal-wrapper/draggable-modal-wrapper.component';
import { UserStateService } from '../../../../shared/services/user-state/user-state.service';
import { DeleteCardOnFileConfirmationMessages } from './constants/delete-card-on-file-confirmation-messages';
import { DeleteCardOnFileConfirmationModalData } from './models/delete-card-on-file-confirmation-modal-data';
import { DeleteCardOnFileDisplayInfo } from './models/delete-card-on-file-display-info';

@Component({
    selector: 'app-delete-card-on-file-confirmation-modal',
    templateUrl: './delete-card-on-file-confirmation-modal.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatButtonModule,
        MatDialogContent,
        MatDialogActions,
        MatDialogTitle,
        DraggableModalWrapperComponent,
        CardTypeIconComponent,
    ],
})
export class DeleteCardOnFileConfirmationModalComponent
    implements OnInit, OnDestroy
{
    private readonly destroy$ = new Subject<void>();

    readonly cardTypeEnum = CardTypeEnum;
    readonly cardTypeLabelMapping = CardTypeLabelMapping;

    authenticatedUser: AuthenticatedUser | null =
        this.userStateService.getUser();

    displayInfo: DeleteCardOnFileDisplayInfo | null = null;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: DeleteCardOnFileConfirmationModalData,
        private readonly alertService: AlertService,
        private readonly userStateService: UserStateService,
        private readonly creditCardService: CreditCardService,
        private readonly dialogRef: MatDialogRef<DeleteCardOnFileConfirmationModalComponent>,
    ) {}

    ngOnInit(): void {
        this.loadDisplayInfo();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Handles the submission of the delete card on file confirmation.
     * Upon successful deletion, displays a success alert and closes the dialog with a true result to indicate successful deletion.
     *
     * @throws Error if card on file ID or authenticated user ID is missing.
     */
    onSubmit(): void {
        this.assertRequiredData();

        this.creditCardService
            .delete(this.authenticatedUser!.id, this.data.cardOnFileId)
            .pipe(
                tap(() => {
                    this.alertService.displaySuccessAlert(
                        DeleteCardOnFileConfirmationMessages.SUCCESS_ALERT_MESSAGE,
                    );
                }),
                tap(() => {
                    // Close the dialog and pass true to indicate successful deletion
                    this.dialogRef.close(true);
                }),
            )
            .subscribe();
    }

    /**
     * Handles the cancellation of the delete card on file action.
     * Closes the dialog with a false result to indicate that the deletion was cancelled.
     */
    onCancel(): void {
        this.dialogRef.close(false);
    }

    private loadDisplayInfo(): void {
        this.assertRequiredData();

        this.creditCardService
            .get(this.authenticatedUser!.id, this.data.cardOnFileId)
            .pipe(
                takeUntil(this.destroy$),
                tap((response: CardOnFileResponse) => {
                    this.displayInfo = {
                        firstName: response.firstName,
                        lastName: response.lastName,
                        cardType: response.cardType,
                        truncatedCardNumber: response.truncatedCardNumber,
                        expirationMonth: formatNumericalMonthToMM(
                            response.expirationMonth,
                        ),
                        expirationYear: response.expirationYear.toString(),
                    };
                }),
            )
            .subscribe();
    }

    /**
     * Asserts that the required data is available to load and save information for the delete card on file confirmation modal.
     *
     * @throws Error if card on file ID or authenticated user ID is missing.
     */
    private assertRequiredData(): void {
        if (!this.data?.cardOnFileId) {
            throw new Error(
                'Card on file ID is required to load display information for the delete card on file confirmation modal.',
            );
        }

        if (!this.authenticatedUser?.id) {
            throw new Error(
                'Authenticated user ID is required to load display information for the delete card on file confirmation modal.',
            );
        }
    }
}
